import { object } from "../util/ObjectUtil.js";

export interface IToastSetting {
    /**
     * The html string, shown in the toast.
     */
    message?: string;
    /**
     * Duration in Milliseconds.
     * If smaller than 32,
     * it will be interpreted as seconds.
     */
    duration?: number;
    /**
     * Additional style classes.
     * To use multiple classes,
     * seperate them with a single whitspace " ".
     */
    styleClass?: string;
    /**
     * Use a key to prevent multiple equal messages
     * showing after each other.
     */
    key?: string;
    /**
     * Updates the matched options.
     */
    updateOnSameKey?: boolean;
    /**
     * When set to true, clicking the toast message,
     * will close it.
     */
    allowClose?: boolean;
    /**
     * Handle click on the toast message.
     */
    onClick?: (toast?: Toast, evt?: MouseEvent) => void;
    /**
     * The parent element of the toast message.
     */
    parent?: HTMLElement | string;
}

export class Toast {
    public static DEFAULT_OPTIONS: IToastSetting = {
        duration: 3000,
        allowClose: true,
        parent: "body",
        onClick: null,
        updateOnSameKey: true
    };

    private _queue: IToastSetting[] = [];
    private _running: boolean = false;
    private _timeoutId: number = 0;

    private static _instance: Toast = null;

    public element: HTMLDivElement;
    public setting: IToastSetting = {};

    constructor() {
        this.element = document.createElement("div");
        this.element.className = "e5_toast";
        this.element.style.visibility = "hidden";
        this.element.style.opacity = "0";
        this.element.addEventListener("click", (evt) => this.handleClick(evt));
    }

    private handleClick(evt: MouseEvent): void {
        if (this.setting.allowClose) {
            this.hide();
        }
        if (typeof this.setting.onClick === "function") {
            this.setting.onClick(this, evt);
        }
    }

    public static showText(message: string, duration: number = 0, key: string = null): void {
        var settings: IToastSetting = { message: message, duration: duration };
        if (key){
            settings.key = key;
        }
        this.show(settings);
    }

    public static show(options: IToastSetting): void {
        this.instance.show(options);
    }

    public show(options: IToastSetting): void {
        //skip if no message defined
        if (!options || !options.message)
            return;

        options = object.merge({}, Toast.DEFAULT_OPTIONS, options) as IToastSetting;

        if (this._running) {
            if (options.key) {
                //Check if the current setting has the same key.
                if (this.setting && this.setting.key == options.key) {
                    if (options.updateOnSameKey) {
                        clearTimeout(this._timeoutId);
                        this.apply(options);
                        this.visible();
                    }
                    return;
                }

                //Check if the an option entry with
                //the same key already exisits.
                for (var i = 0; i < this._queue.length; ++i) {
                    var o = this._queue[i];
                    if (o.key == options.key) {
                        if (options.updateOnSameKey)
                            this._queue[i] = options;
                        return;
                    }
                }
            }
            this._queue.push(options);
            return;
        }

        this._running = true;
        this.apply(options);
        this.visible();
    }

    private apply(setting: IToastSetting): void {
        this.setting = setting;

        var parent: HTMLElement = null;
        if (setting.parent === undefined) {
            parent = document.body;
        }
        else if (typeof setting.parent === "string") {
            parent = document.querySelector(setting.parent);
        } else {
            parent = setting.parent;
        }

        if (!parent) {
            console.warn("Could not find parent for toast: '" + setting.parent + "'. Use body as fallback.");
            parent = document.body;
        }
        parent.appendChild(this.element);

        this.element.className = "e5_toast";
        this.element.setAttribute("data-toastkey", this.setting.key ? this.setting.key : "");

        this.element.innerHTML = setting.message;

        if (setting.styleClass) {
            var classes = setting.styleClass.trim().split(" ");
            for (var cls of classes) {
                if (cls)
                    this.element.classList.add(cls);
            }
        }

        var dur = setting.duration ? setting.duration : Toast.DEFAULT_OPTIONS.duration;
        //If the duration is given in seconds instead of milliseconds.
        if (dur < 500) {
            dur *= 1000;
        }
        this._timeoutId = <any>setTimeout(() => this.hide(), dur);
    }

    private visible(): void {
        gsap.to(this.element, { duration: 0.3, autoAlpha: 1 });
    }

    private hide(key: string = null): void {
        if (key !== null && this.setting.key != key)
            return;
        clearTimeout(this._timeoutId);
        gsap.to(this.element, { duration: 0.3, autoAlpha: 0, onComplete: () => this.next() });
    }

    public static hide(key: string = null): void {
        this.instance.hide(key);
    }

    private static get instance(): Toast {
        if (this._instance)
            return this._instance;
        this._instance = new Toast();
        return this._instance;
    }

    private next(): void {
        if (this._queue.length > 0) {
            var setting = this._queue.shift();
            this.apply(setting);
            this.visible();
        }
        else {
            this._running = false;
        }
    }
}